/**
 * Cross-cutting reactive variables that drive Global Search *navigation*
 * intent (open / reopen-on-path).
 *
 * These live in the shared `@/stores` layer — not inside
 * `@/features/global-search` — because consumers outside that feature need to
 * read/write them (e.g. the player-detail Breadcrumb molecule). Importing them
 * through the feature barrel from a molecule that itself renders inside the
 * Global Search modal's preview→header chain forms an `import/no-cycle`; a
 * cycle-free shared module avoids that while keeping the deep-feature-import
 * lint rule satisfied. `globalSearchStore` re-exports these for the feature's
 * own consumers, so existing import sites are unaffected.
 * @module stores/globalSearchNavigation
 */

import { makeVar } from "@apollo/client";

/** Whether the global search modal is open */
export const globalSearchOpenVar = makeVar<boolean>(false);

/**
 * Pending "reopen the search overlay once we land on this pathname" intent.
 *
 * SE-5387 — the breadcrumb "Search" crumb must reopen the Global Search
 * overlay over the tab the user came from. Opening the modal in the SAME tick
 * as `router.navigate` races the SE-5185 teardown effect, which closes the
 * overlay on any pathname change while it is open — so a same-tick reopen is
 * immediately stomped. Instead the breadcrumb stores the destination pathname
 * here; GlobalSearchModalContainer opens the modal only AFTER navigation has
 * settled on that pathname (a later commit where the route is no longer
 * changing), so the reopen survives the route transition. Holds `null` when no
 * reopen is pending.
 */
export const globalSearchReopenForPathVar = makeVar<string | null>(null);
